import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
 
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount / 100);
};

/**
 * Formats a price amount with the specified currency.
 * Uses Intl.NumberFormat for proper localization.
 * 
 * @param amount - The price amount (as a number, not divided by 100)
 * @param currency - ISO 4217 currency code (e.g., 'EUR', 'USD', 'RON'). Defaults to 'RON'
 * @param locale - Optional locale string (e.g., 'ro-RO', 'en-US'). Defaults to browser locale
 * @returns Formatted price string (e.g., "150,00 RON" or "$150.00")
 */
export const formatPrice = (
  amount: number, 
  currency: string = 'RON',
  locale?: string
): string => {
  // For USD, always use 'en-US' locale to get "$" instead of "US$"
  // For other currencies, use provided locale or browser default
  const formatLocale = currency === 'USD' 
    ? 'en-US' 
    : (locale || (typeof navigator !== 'undefined' ? navigator.language : 'en-US'));
  
  try {
    const formatted = new Intl.NumberFormat(formatLocale, {
      style: 'currency',
      currency: currency,
      currencyDisplay: 'symbol', // Use symbol instead of name (e.g., "$" instead of "US$")
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
    
    // For USD, ensure we only show "$" not "US$"
    if (currency === 'USD' && formatted.includes('US$')) {
      return formatted.replace('US$', '$');
    }
    
    return formatted;
  } catch (error) {
    // Fallback if currency is invalid or Intl.NumberFormat fails
    console.warn(`[formatPrice] Error formatting price with currency ${currency}:`, error);
    return `${amount.toFixed(2)} ${currency}`;
  }
};

export const formatDate = (date: Date | string, locale: string = 'ro'): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const dateLocale = locale === 'ro' ? ro : undefined;
  return format(d, 'EEEE, d MMMM yyyy', { locale: dateLocale });
};

export const formatTime = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

export const getInitials = (firstName: string, lastName: string): string => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const getGreeting = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

export const generateTimeSlots = (
  startHour: number = 9, 
  endHour: number = 17, 
  intervalMinutes: number = 30
): string[] => {
  const slots: string[] = [];
  const totalMinutesInDay = (endHour - startHour) * 60;
  const totalSlots = totalMinutesInDay / intervalMinutes;
  
  for (let i = 0; i <= totalSlots; i++) {
    const minutes = i * intervalMinutes;
    const hour = Math.floor(minutes / 60) + startHour;
    const minute = minutes % 60;
    
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const formattedHour = hour % 12 || 12;
    const formattedMinute = minute.toString().padStart(2, '0');
    
    slots.push(`${formattedHour}:${formattedMinute} ${ampm}`);
  }
  
  return slots;
};

export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isValidPhone = (phone: string): boolean => {
  const phoneRegex = /^\d{10}$/;
  return phoneRegex.test(phone.replace(/[^\d]/g, ''));
};

export const formatPhone = (phone: string): string => {
  const cleaned = phone.replace(/[^\d]/g, '');
  if (cleaned.length !== 10) return phone;
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
};

/** Digits-only phone for tel: links */
export const phoneToTelHref = (phone: string): string =>
  `tel:${phone.replace(/\s/g, '')}`;

/** Romanian-aware wa.me number from contact phone */
export const phoneToWhatsAppHref = (
  phone: string,
  options?: { prefillMessage?: string }
): string => {
  let digits = phone.replace(/\s/g, '').replace(/\+/g, '');
  if (digits.startsWith('0')) {
    digits = '40' + digits.substring(1);
  } else if (!digits.startsWith('40') && digits.length <= 10) {
    digits = '40' + digits;
  }
  const base = `https://wa.me/${digits}`;
  if (options?.prefillMessage) {
    return `${base}?text=${encodeURIComponent(options.prefillMessage)}`;
  }
  return base;
};

/**
 * Extracts the body content from a complete HTML document for safe rendering within existing layouts
 * @param htmlContent - Complete HTML document string 
 * @returns Only the inner body content without html/head/body tags
 */
export function extractBodyContent(htmlContent: string): string {
  if (!htmlContent) return '';
  
  try {
    // Create a temporary DOM element to parse the HTML
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = htmlContent;
    
    // Try to find the body element first
    const bodyElement = tempContainer.querySelector('body');
    if (bodyElement) {
      return bodyElement.innerHTML;
    }
    
    // If no body element, check if it's already just content (no full HTML structure)
    const hasHtmlStructure = htmlContent.includes('<!DOCTYPE') || 
                            htmlContent.includes('<html') || 
                            htmlContent.includes('<head>') || 
                            htmlContent.includes('<body>');
    
    if (!hasHtmlStructure) {
      // It's already just content, return as-is
      return htmlContent;
    }
    
    // Fallback: try to extract content between body tags using regex
    const bodyMatch = htmlContent.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    if (bodyMatch) {
      return bodyMatch[1];
    }
    
    // Last fallback: return the content as-is but log a warning
    console.warn('Could not extract body content, returning original HTML');
    return htmlContent;
    
  } catch (error) {
    console.error('Error extracting body content:', error);
    return htmlContent; // Return original on error
  }
}

/**
 * Checks if content is a complete HTML document
 * @param content - HTML content to check
 * @returns true if it's a complete HTML document, false otherwise
 */
export function isCompleteHtmlDocument(content: string): boolean {
  if (!content) return false;
  
  const lowerContent = content.toLowerCase();
  return lowerContent.includes('<!doctype') || 
         (lowerContent.includes('<html') && lowerContent.includes('<body>'));
}

/** Darkens a hex color by mixing it toward black (amount 0–1). */
export function mixWithBlack(hex: string, amount: number): string {
  const h = hex.replace('#', '');
  if (h.length !== 6) return '#0b1220';
  const t = Math.min(1, Math.max(0, amount));
  const r = Math.round(parseInt(h.slice(0, 2), 16) * (1 - t));
  const g = Math.round(parseInt(h.slice(2, 4), 16) * (1 - t));
  const b = Math.round(parseInt(h.slice(4, 6), 16) * (1 - t));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/** Primary dark — used for section backgrounds and CTA hover states. */
export function getPrimaryDarkColor(hex: string, amount = 0.35): string {
  return mixWithBlack(hex, amount);
}

export function hexToHsl(hex: string): string {
  // Remove the hash at the start if it's there
  hex = hex.replace(/^#/, '');

  // Parse r, g, b values
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  // Convert r, g, b to range 0-1
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);

  let h = 0;
  let s = 0;
  let l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);

  return `${h} ${s}% ${l}%`;
}

/**
 * Converts an HSL color string (e.g., "210 40% 98%") to a hex color string.
 * @param hsl The HSL color string.
 * @returns The hex color string.
 */
export function hslStringToHex(hsl: string): string {
  if (!hsl) {
    // Return a default, safe color if the input is empty or null
    return '#000000'; 
  }

  const hslValues = hsl.match(/\d+(\.\d+)?/g);

  if (!hslValues || hslValues.length < 3) {
    // If parsing fails, log a warning and return a safe default
    console.warn(`Invalid HSL string format: "${hsl}". Defaulting to black.`);
    return '#000000';
  }

  const [h, s, l] = hslValues.map(Number);
  
  const sPercentage = s / 100;
  const lPercentage = l / 100;

  const c = (1 - Math.abs(2 * lPercentage - 1)) * sPercentage;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = lPercentage - c/2;
  let r = 0, g = 0, b = 0;

  if (h >= 0 && h < 60) {
    r = c; g = x; b = 0;
  } else if (h >= 60 && h < 120) {
    r = x; g = c; b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0; g = c; b = x;
  } else if (h >= 180 && h < 240) {
    r = 0; g = x; b = c;
  } else if (h >= 240 && h < 300) {
    r = x; g = 0; b = c;
  } else if (h >= 300 && h < 360) {
    r = c; g = 0; b = x;
  }
  
  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);

  const toHex = (c: number) => ('0' + c.toString(16)).slice(-2);

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Gets the string value of a CSS custom property from the root element.
 * @param varName The name of the CSS custom property (e.g., '--primary').
 * @returns The value of the property, or null if not in a browser environment.
 */
export function getCssVarValue(varName: string): string | null {
  if (typeof window === 'undefined') return null;
  return getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
}

/**
 * Calculates the luminance of a color and returns the optimal text color
 * @param backgroundColor - The background color in hex format (e.g., "#ff0000")
 * @returns "white" for dark backgrounds, "black" for light backgrounds
 */
export function getContrastTextColor(backgroundColor: string): "white" | "black" {
  // Remove # if present
  const hex = backgroundColor.replace('#', '');
  
  // Convert hex to RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Calculate luminance using the formula from WCAG 2.0
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Return white for dark backgrounds, black for light backgrounds
  return luminance > 0.5 ? "black" : "white";
}

/** True when a hex/rgb background reads as dark (for image placeholder surfaces). */
export function isDarkBackgroundColor(backgroundColor: string): boolean {
  if (!backgroundColor || backgroundColor === 'transparent') {
    return false;
  }
  const trimmed = backgroundColor.trim();
  if (trimmed.startsWith('#') && trimmed.length >= 4) {
    return getContrastTextColor(trimmed) === 'white';
  }
  const rgbMatch = trimmed.match(
    /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i
  );
  if (rgbMatch) {
    const r = Number(rgbMatch[1]);
    const g = Number(rgbMatch[2]);
    const b = Number(rgbMatch[3]);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance <= 0.5;
  }
  return false;
}

const IMAGE_SURFACE_LIGHT = {
  bg: '#e5e7eb',
  pulse: '#d1d5db',
  errorText: '#9ca3af',
} as const;

const IMAGE_SURFACE_DARK = {
  errorText: 'rgba(255, 255, 255, 0.75)',
} as const;

/** Sets --image-surface-* tokens and body class for loading placeholders. */
export function applyImageSurfaceTheme(
  root: HTMLElement,
  options: { dark: boolean }
): void {
  if (options.dark) {
    root.style.setProperty('--image-surface-bg', 'transparent');
    root.style.setProperty('--image-surface-pulse', 'transparent');
    root.style.setProperty('--image-surface-error-text', IMAGE_SURFACE_DARK.errorText);
  } else {
    root.style.setProperty('--image-surface-bg', IMAGE_SURFACE_LIGHT.bg);
    root.style.setProperty('--image-surface-pulse', IMAGE_SURFACE_LIGHT.pulse);
    root.style.setProperty('--image-surface-error-text', IMAGE_SURFACE_LIGHT.errorText);
  }

  document.body.classList.toggle('theme-tone-dark', options.dark);
}

/**
 * Gets CSS custom property value for brand color
 * @returns The brand color value or fallback
 */
export function getBrandColor(): string {
  if (typeof window !== 'undefined') {
    try {
      const brandColor = getComputedStyle(document.documentElement)
        .getPropertyValue('--brand-color')
        .trim();
      
      return brandColor || '#3b82f6'; // fallback to blue
    } catch (e) {
      // Fallback if getPropertyValue fails (e.g., in certain browser contexts)
      return '#3b82f6';
    }
  }
  return '#3b82f6';
}
